import { Box, Paper, Typography } from "@mui/material";
import type { ReactNode } from "react";

import type { CrmObject } from "../../generated/graphql";
import { CRM_COLORS } from "../../lib/crm";
import { CrmObjectIcon } from "./CrmObjectIcon";

type CrmPageHeaderProps = {
  object?: CrmObject;
  icon?: ReactNode;
  overline: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  // Highlights, a path or list controls under the title.
  children?: ReactNode;
};

// The Salesforce page header: object icon, object label above the record or
// list name, actions on the right.
export const CrmPageHeader = ({ object, icon, overline, title, meta, actions, children }: CrmPageHeaderProps) => (
  <Paper
    elevation={0}
    sx={{
      mb: "12px",
      backgroundColor: CRM_COLORS.headerBackground,
      border: `1px solid ${CRM_COLORS.cardBorder}`,
      borderRadius: "4px",
      boxShadow: CRM_COLORS.cardShadow,
      color: CRM_COLORS.text,
    }}
  >
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: "16px", p: "16px" }}>
      <Box sx={{ display: "flex", flex: 1, gap: "12px", minWidth: 0 }}>
        {icon ?? (object && <CrmObjectIcon object={object} size="large" />)}
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: "13px", lineHeight: 1.25, color: CRM_COLORS.textWeak }}>{overline}</Typography>
          <Typography
            component="h1"
            sx={{ m: 0, fontSize: "20px", fontWeight: 700, lineHeight: 1.25, overflowWrap: "anywhere" }}
          >
            {title}
          </Typography>
          {meta && <Box sx={{ mt: "4px", fontSize: "12px", color: CRM_COLORS.textWeak }}>{meta}</Box>}
        </Box>
      </Box>
      {actions && <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>{actions}</Box>}
    </Box>
    {children}
  </Paper>
);
